// This handles both the recovery email link and a new recovery request. The token is removed from the address bar before the redemption request, reducing accidental exposure through copied URLs or browser history

import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";

import {
  useRequestCheckoutRecoveryMutation,
  useRedeemCheckoutRecoveryMutation,
} from "../../features/api/checkoutApiSlice";

import "./checkoutConfirmation.scss";

export default function CheckoutRecovery() {
  const [params] = useSearchParams();

  const token = params.get("token") || "";
  const initialSessionId = params.get("checkoutSessionId") || "";

  const [email, setEmail] = useState("");
  const [checkoutSessionId, setCheckoutSessionId] = useState(initialSessionId);

  const [requested, setRequested] = useState(false);
  const [recoveredId, setRecoveredId] = useState("");
  const [recoveryError, setRecoveryError] = useState("");

  const attemptedToken = useRef(null);

  const [requestRecovery, { isLoading: isRequesting }] =
    useRequestCheckoutRecoveryMutation();

  const [redeemRecovery, { isLoading: isRedeeming }] =
    useRedeemCheckoutRecoveryMutation();

  useEffect(() => {
    if (!token || attemptedToken.current === token) {
      return;
    }

    attemptedToken.current = token;

    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname
    );

    if (!/^[a-f0-9]{64}$/.test(token)) {
      setRecoveryError("This recovery link is invalid.");
      return;
    }

    redeemRecovery({ token })
      .unwrap()
      .then((result) => {
        setRecoveredId(result.checkoutSessionId);
      })
      .catch(() => {
        setRecoveryError(
          "This recovery link is invalid, expired, or already used. Request a new link below."
        );
      });
  }, [token, redeemRecovery]);

  const handleRequest = async (event) => {
    event.preventDefault();
    setRecoveryError("");

    try {
      await requestRecovery({
        email: email.trim(),
        checkoutSessionId: checkoutSessionId.trim(),
      }).unwrap();

      setRequested(true);
    } catch (error) {
      setRecoveryError(
        error?.data?.message ||
          "Unable to submit your request. Please try again."
      );
    }
  };

  return (
    <main className="checkout-confirmation">
      <div className="checkout-confirmation__shell">
        <section className="checkout-confirmation__panel">
          <p className="checkout-confirmation__eyebrow">
            MINI MILES ADVENTURES · GEAR SHOP
          </p>

          <h1>Recover your order</h1>

          {recoveredId ? (
            <>
              <p>Your order access has been restored in this browser.</p>

              <Link
                className="checkout-confirmation__button"
                to={`/checkout/confirmation?checkoutSessionId=${encodeURIComponent(
                  recoveredId
                )}`}
              >
                View your order
              </Link>
            </>
          ) : isRedeeming ? (
            <p role="status">Verifying your recovery link…</p>
          ) : (
            <>
              {recoveryError && (
                <p className="checkout-confirmation__error" role="alert">
                  {recoveryError}
                </p>
              )}

              {requested ? (
                <p role="status">
                  If a matching paid guest order exists, a recovery link will be
                  emailed. Please check your inbox and spam folder.
                </p>
              ) : (
                <>
                  <p>
                    Enter the email address used at checkout and your checkout
                    reference. We'll email a secure link if we find a matching
                    paid guest order.
                  </p>

                  <form
                    onSubmit={handleRequest}
                    className="checkout-confirmation__form"
                  >
                    <label htmlFor="recovery-email">Checkout email</label>

                    <input
                      id="recovery-email"
                      type="email"
                      autoComplete="email"
                      maxLength={254}
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                    />

                    <label htmlFor="recovery-session">Checkout reference</label>

                    <input
                      id="recovery-session"
                      type="text"
                      value={checkoutSessionId}
                      onChange={(event) =>
                        setCheckoutSessionId(event.target.value)
                      }
                      pattern="[a-fA-F0-9]{24}"
                      title="Enter the 24-character checkout reference"
                      required
                    />

                    <button
                      className="checkout-confirmation__button"
                      type="submit"
                      disabled={isRequesting}
                    >
                      {isRequesting
                        ? "Sending request…"
                        : "Email recovery link"}
                    </button>
                  </form>
                </>
              )}
            </>
          )}

          <p className="checkout-confirmation__back">
            <Link to="/shop">Back to the shop</Link>
          </p>
        </section>
      </div>
    </main>
  );
}
